import { api } from "@/v5/lib/api";
import {
  type FilterConfig,
  type FilterNegations,
  type FilterOption,
  type FilterValue,
  type DateRangeValue,
  type RangeValue,
  isDateRange,
  isRange,
  isSelect,
} from "@/v5/types/filters";
import { isDateRangePresetKey, resolvePreset } from "@/lib/dateRangePresets";
import { useCallback, useEffect, useRef, useState } from "react";

function defaultForConfig(c: FilterConfig): FilterValue {
  if (c.type === "daterange") return { from: "", to: "" } as DateRangeValue;
  if (c.type === "range") return { min: c.min ?? 0, max: c.max ?? 120 } as RangeValue;
  return [] as string[];
}

/**
 * Persist format mirrors legacy `save-filters.php`:
 *   `<csv-values>;;;;<plus>` where plus="true" means positive, plus="false"
 *   means negative (NOT IN). Default is "true".
 */
function serializeForPersist(
  _key: string,
  val: FilterValue,
  negate: boolean,
): string {
  if (isDateRange(val)) return `${val.from},${val.to},${val.preset ?? ""}`;
  if (isRange(val)) return `${val.min},${val.max}`;
  if (isSelect(val)) return val.join(",") + ";;;;" + (negate ? "false" : "true");
  return "";
}

interface DeserializedSelect {
  values: FilterValue;
  negate: boolean;
}

function deserializeFromPersist(
  config: FilterConfig,
  raw: string[],
): DeserializedSelect {
  // `serializeForPersist` always joins daterange/range into a single
  // comma-separated string, which the API stores as one row. So `raw` will
  // typically have length 1 here. Parse from the joined form to handle that
  // (and any legacy multi-row format) uniformly.
  if (config.type === "daterange") {
    const parts = raw.join(",").split(",");
    const from = parts[0] ?? "";
    const to = parts[1] ?? "";
    const presetRaw = parts[2] ?? "";
    const preset = isDateRangePresetKey(presetRaw) ? presetRaw : undefined;
    if (preset) {
      const resolved = resolvePreset(preset);
      return {
        values: { from: resolved.from, to: resolved.to, preset },
        negate: false,
      };
    }
    return { values: { from, to }, negate: false };
  }
  if (config.type === "range") {
    const parts = raw.join(",").split(",");
    return {
      values: { min: Number(parts[0]) || 0, max: Number(parts[1]) || 120 },
      negate: false,
    };
  }
  const plusSplit = raw.join(",").split(";;;;");
  const vals = plusSplit[0].split(",").filter(Boolean);
  // Legacy semantics: plusPart === "false" => negate
  const negate = (plusSplit[1] ?? "").trim() === "false";
  return { values: vals, negate };
}

export function buildApiFilterString(
  values: Record<string, FilterValue>,
  negations: FilterNegations = {},
): { filtersArray: string; filterPlus: string } {
  const parts: string[] = [];
  const plusParts: string[] = [];

  for (const [key, val] of Object.entries(values)) {
    if (isDateRange(val)) {
      if (val.from || val.to) parts.push(`daterange:${val.from},${val.to}`);
    } else if (isRange(val)) {
      if (val.min > 0 || val.max < 120)
        parts.push(`${key}:${val.min},${val.max}`);
    } else if (isSelect(val) && val.length > 0) {
      parts.push(`${key}:${val.join(",")}`);
      // Legacy filterPlus contract: 0 == positive (IN), 1 == negative (NOT IN)
      plusParts.push(`${key}:${negations[key] ? 1 : 0}`);
    }
  }

  return { filtersArray: parts.join(";"), filterPlus: plusParts.join(";") };
}

interface LaravelFiltersResponse {
  data: { filters: Record<string, string[]>; filterTypes?: string[] };
  meta: unknown;
}

interface LaravelOptionsResponse {
  data: Record<string, FilterOption[]>;
  meta: unknown;
}

interface UseFiltersReturn {
  values: Record<string, FilterValue>;
  negations: FilterNegations;
  options: Record<string, FilterOption[]>;
  loading: boolean;
  setFilter: (key: string, value: FilterValue) => void;
  setNegation: (key: string, negate: boolean) => void;
  clearFilter: (key: string) => void;
  clearAll: () => void;
}

export function useFilters(
  pageUrl: string,
  configs: FilterConfig[],
): UseFiltersReturn {
  const [values, setValues] = useState<Record<string, FilterValue>>(() => {
    const init: Record<string, FilterValue> = {};
    for (const c of configs) init[c.key] = defaultForConfig(c);
    return init;
  });
  const [negations, setNegations] = useState<FilterNegations>({});
  const [options, setOptions] = useState<Record<string, FilterOption[]>>({});
  const [loading, setLoading] = useState(true);
  const configsRef = useRef(configs);
  configsRef.current = configs;
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const negationsRef = useRef(negations);
  negationsRef.current = negations;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const selectTypes = configs
          .filter((c) => c.type === "select")
          .map((c) => c.key);

        const [savedResult, optionsResult] = await Promise.allSettled([
          api<LaravelFiltersResponse>("/filters/persist", {
            params: { pageUrl, viewType: "1" },
          }),
          selectTypes.length > 0
            ? api<LaravelOptionsResponse>("/filters/options", {
                params: Object.fromEntries(
                  selectTypes.map((t, i) => [`types[${i}]`, t]),
                ),
              })
            : Promise.resolve({ data: {}, meta: [] } as LaravelOptionsResponse),
        ]);

        if (cancelled) return;

        const optionsRes = optionsResult.status === "fulfilled" ? optionsResult.value.data : null;
        const savedRes = savedResult.status === "fulfilled" ? savedResult.value.data : null;

        if (optionsRes) setOptions(optionsRes);

        if (savedRes?.filters) {
          const restoredValues: Record<string, FilterValue> = {};
          const restoredNegations: FilterNegations = {};
          for (const c of configsRef.current) {
            const raw = savedRes.filters[c.key];
            if (raw && raw.length > 0) {
              const { values: v, negate } = deserializeFromPersist(c, raw);
              restoredValues[c.key] = v;
              if (negate) restoredNegations[c.key] = true;
            } else {
              restoredValues[c.key] = defaultForConfig(c);
            }
          }
          setValues(restoredValues);
          setNegations(restoredNegations);
        }
      } catch (err) {
        console.error("useFilters: failed to load", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [pageUrl, configs]);

  const persistFilter = useCallback(
    (key: string, val: FilterValue, negate: boolean) => {
      const serialized = serializeForPersist(key, val, negate);
      api("/filters/persist", {
        method: "POST",
        body: {
          filterType: key,
          filterValue: serialized,
          pageUrl,
          viewType: "1",
        },
      }).catch((err) =>
        console.error("useFilters: failed to persist", key, err),
      );
    },
    [pageUrl],
  );

  const setFilter = useCallback(
    (key: string, value: FilterValue) => {
      setValues((prev) => ({ ...prev, [key]: value }));
      persistFilter(key, value, negationsRef.current[key] ?? false);
    },
    [persistFilter],
  );

  const setNegation = useCallback(
    (key: string, negate: boolean) => {
      setNegations((prev) => {
        if ((prev[key] ?? false) === negate) return prev;
        const next = { ...prev };
        if (negate) next[key] = true;
        else delete next[key];
        return next;
      });
      const currentValue =
        valuesRef.current[key] ??
        defaultForConfig(
          configsRef.current.find((c) => c.key === key) ?? {
            key,
            label: key,
            type: "select",
          },
        );
      persistFilter(key, currentValue, negate);
    },
    [persistFilter],
  );

  const clearFilter = useCallback(
    (key: string) => {
      const config = configsRef.current.find((c) => c.key === key);
      if (!config) return;
      const def = defaultForConfig(config);
      setValues((prev) => ({ ...prev, [key]: def }));
      setNegations((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      persistFilter(key, def, false);
    },
    [persistFilter],
  );

  const clearAll = useCallback(() => {
    const cleared: Record<string, FilterValue> = {};
    for (const c of configsRef.current) {
      const def = defaultForConfig(c);
      cleared[c.key] = def;
      persistFilter(c.key, def, false);
    }
    setValues(cleared);
    setNegations({});
  }, [persistFilter]);

  return {
    values,
    negations,
    options,
    loading,
    setFilter,
    setNegation,
    clearFilter,
    clearAll,
  };
}
